import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeftIcon,
  QrCodeIcon,
  UserGroupIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../../context/AuthContext";
import { attendanceService } from "../../services/attendanceService";
import { soundService } from "../../services/soundService";
import QRScanner from "../../components/QRScanner";

const AdminEntrancePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [entranceMode, setEntranceMode] = useState(false);
  const [attendanceCount, setAttendanceCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastScannedUrl, setLastScannedUrl] = useState<string | null>(null);
  const [showResetButton, setShowResetButton] = useState(false);
  const [clickCount, setClickCount] = useState(0);

  // Load entrance mode from localStorage and fetch current count
  useEffect(() => {
    const savedMode = localStorage.getItem("entranceMode") === "true";
    setEntranceMode(savedMode);

    fetchAttendanceCount();
  }, []);

  const fetchAttendanceCount = async () => {
    try {
      const response = await attendanceService.getAttendanceCount();
      setAttendanceCount(response.data.checkInCount);
    } catch (error) {
      console.error("Failed to fetch attendance count:", error);
      setError("Failed to load attendance count");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleEntranceMode = () => {
    const newMode = !entranceMode;
    setEntranceMode(newMode);
    localStorage.setItem("entranceMode", newMode.toString());

    if (newMode) {
      setError(null);
    }
  };

  const handleManualCheckIn = async () => {
    try {
      setError(null);
      const response = await attendanceService.pingAttendance();
      setAttendanceCount(response.data.checkInCount);

      // Show success animation
      setShowSuccess(true);
      soundService.playNotificationSound();

      setTimeout(() => setShowSuccess(false), 2000);
    } catch (error: any) {
      console.error("Manual check-in failed:", error);
      setError(error.response?.data?.message || "Failed to record attendance");
    }
  };

  const handleReset = async () => {
    if (window.confirm("Are you sure you want to reset the attendance count to 0?")) {
      try {
        await attendanceService.resetAttendance();
        setAttendanceCount(0);
        soundService.playSuccessSound();
      } catch (error: any) {
        console.error("Reset failed:", error);
        setError(error.response?.data?.message || "Failed to reset attendance count");
      }
    }
  };

  const handleQRScan = async (scannedData: string) => {
    console.log("QR Code scanned:", scannedData);

    // Additional duplicate prevention at page level (reduced to 1.5 seconds for faster scanning)
    if (lastScannedUrl === scannedData) {
      console.log("Duplicate scan ignored at page level");
      return;
    }

    setLastScannedUrl(scannedData);
    setTimeout(() => setLastScannedUrl(null), 1500); // Reduced to 1.5 seconds for faster workflow

    // Check if the scanned URL contains our domain (for validation)
    const isValidUrl = scannedData.includes('localhost:3000') ||
      scannedData.includes('visa-connect-ethiopia') ||
      scannedData.includes('visa-event-frontend');

    if (!isValidUrl) {
      setError("Invalid QR code. Please scan a valid event card.");
      setTimeout(() => setError(null), 2500);
      return;
    }

    // If entrance mode is enabled, automatically check in
    if (entranceMode) {
      try {
        setError(null);

        // Show immediate feedback with faster animation
        setShowSuccess(true);
        soundService.playNotificationSound();

        // Make API call in parallel (don't wait for it to show success)
        attendanceService.pingAttendance().then(response => {
          setAttendanceCount(response.data.checkInCount);
        }).catch(error => {
          console.error("QR scan check-in failed:", error);
          setShowSuccess(false);
          setError(error.response?.data?.message || "Failed to record attendance");
          setTimeout(() => setError(null), 3000);
        });

        // Keep success animation for 1 second (much faster feedback)
        setTimeout(() => setShowSuccess(false), 1000);
      } catch (error: any) {
        console.error("QR scan check-in failed:", error);
        setShowSuccess(false);
        setError(error.response?.data?.message || "Failed to record attendance");
        setTimeout(() => setError(null), 3000);
      }
    } else {
      setError("Entrance mode is not enabled. Please enable it first.");
      setTimeout(() => setError(null), 2500);
    }
  };

  const handleScanError = (error: string) => {
    console.error("QR Scanner error:", error);
    setError(`Scanner error: ${error}`);
    setTimeout(() => setError(null), 5000);
  };

  // Secret access to reset button - click the attendance count 5 times quickly
  const handleAttendanceCountClick = () => {
    setClickCount(prev => prev + 1);

    if (clickCount + 1 >= 5) {
      setShowResetButton(true);
      setClickCount(0);
      // Hide the reset button after 10 seconds for security
      setTimeout(() => {
        setShowResetButton(false);
      }, 10000);
    }

    // Reset click count after 3 seconds of inactivity
    setTimeout(() => {
      setClickCount(0);
    }, 3000);
  };
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <motion.header
        className="bg-white border-b border-gray-200 sticky top-0 z-40"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={() => navigate("/admin/dashboard")}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">
                  Entrance Scanner
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                  Track event attendance in real-time
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="text-right hidden md:block">
                <p className="text-sm font-medium text-gray-900">
                  {user?.email}
                </p>
                <p className="text-xs text-gray-500">Admin</p>
              </div>
              <button
                onClick={logout}
                className="btn-secondary text-xs sm:text-sm px-2 sm:px-4 py-2"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 pb-20 sm:pb-8">
        {/* Success Flash - Ultra fast and responsive */}
        {showSuccess && (
          <motion.div
            className="fixed inset-0 bg-green-500 bg-opacity-95 flex items-center justify-center z-50"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.15 }}
          >
            <div className="text-center text-white">
              <motion.div
                initial={{ scale: 0.3 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.2, type: "spring", bounce: 0.6 }}
              >
                <CheckCircleIcon className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-2 sm:mb-3" />
              </motion.div>
              <motion.h2
                className="text-xl sm:text-3xl font-bold mb-1"
                initial={{ y: 15, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.05, duration: 0.15 }}
              >
                ✅ +1
              </motion.h2>
              <motion.p
                className="text-sm sm:text-lg"
                initial={{ y: 15, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.15 }}
              >
                Scanned!
              </motion.p>
            </div>
          </motion.div>
        )}

        {/* Error Message */}
        {error && (
          <motion.div
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <XCircleIcon className="h-5 w-5 text-red-600 mr-2" />
            <span className="text-red-800">{error}</span>
          </motion.div>
        )}

        <div className="space-y-4 sm:space-y-6">
          {/* Current Count Card */}
          <motion.div
            className="card p-4 sm:p-6 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <UserGroupIcon className="w-12 h-12 sm:w-16 sm:h-16 text-blue-600 mx-auto mb-3 sm:mb-4" />
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
              Current Attendance
            </h2>
            <p
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-blue-600 mb-3 sm:mb-4 cursor-pointer select-none"
              onClick={handleAttendanceCountClick}
              title="Click 5 times quickly for admin options"
            >
              {attendanceCount}
            </p>
            <p className="text-sm sm:text-base text-gray-600">People checked in</p>
            {showResetButton && (
              <motion.div
                className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2 }}
              >
                <p className="text-xs text-red-600 mb-2">⚠️ Admin Access Unlocked (10s)</p>
                <button
                  onClick={handleReset}
                  className="btn-danger text-sm px-4 py-2"
                >
                  🗑️ Reset Count
                </button>
              </motion.div>
            )}
          </motion.div>

          {/* Entrance Mode Toggle */}
          <motion.div
            className="card p-4 sm:p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-start sm:items-center justify-between mb-4 gap-4">
              <div className="flex-1">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
                  Entrance Mode
                </h3>
                <p className="text-xs sm:text-sm text-gray-600">
                  Toggle to start/stop QR scanning automatically
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                <input
                  type="checkbox"
                  checked={entranceMode}
                  onChange={toggleEntranceMode}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {entranceMode && (
              <motion.div
                className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center">
                  <QrCodeIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2 flex-shrink-0" />
                  <span className="text-blue-800 font-medium text-sm sm:text-base">
                    Entrance mode is active - QR scans will auto-count
                  </span>
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* QR Scanner */}
          {entranceMode && (
            <motion.div
              className="card p-3 sm:p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 text-center">
                📱 QR Code Scanner
              </h3>
              <QRScanner
                onScan={handleQRScan}
                onError={handleScanError}
                isActive={entranceMode}
              />
            </motion.div>
          )}
          {/* Manual Controls */}
          <motion.div
            className="card p-4 sm:p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
              Manual Controls
            </h3>
            <div className="flex justify-center">
              <button
                onClick={handleManualCheckIn}
                className="btn-primary flex-1 text-sm sm:text-base py-3 sm:py-2"
              >
                Manual Check-in (+1)
              </button>
            </div>
          </motion.div>

          {/* Instructions */}
          <motion.div
            className="card p-4 sm:p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
              📋 How to Use
            </h3>
            <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-gray-600">
              <div className="flex items-start space-x-2 sm:space-x-3">
                <span className="bg-blue-100 text-blue-800 rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  1
                </span>
                <p>Toggle "Entrance Mode" on to start scanning</p>
              </div>
              <div className="flex items-start space-x-2 sm:space-x-3">
                <span className="bg-blue-100 text-blue-800 rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  2
                </span>
                <p>Camera will automatically start when mode is enabled</p>
              </div>
              <div className="flex items-start space-x-2 sm:space-x-3">
                <span className="bg-blue-100 text-blue-800 rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  3
                </span>
                <p>Point camera at QR codes on guest cards</p>
              </div>
              <div className="flex items-start space-x-2 sm:space-x-3">
                <span className="bg-blue-100 text-blue-800 rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  4
                </span>
                <p>Each scan automatically adds +1 to attendance</p>
              </div>
              <div className="flex items-start space-x-2 sm:space-x-3">
                <span className="bg-blue-100 text-blue-800 rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  5
                </span>
                <p>Use manual check-in if QR code scanners fail</p>
              </div>
              <div className="flex items-start space-x-2 sm:space-x-3">
                <span className="bg-blue-100 text-blue-800 rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  6
                </span>
                <p>Toggle mode off to stop scanning</p>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default AdminEntrancePage; 